import { useCallback, useEffect, useState } from "react";

import { AiOutlineCar, AiOutlinePlus } from "react-icons/ai";

import {
  createVehicle,
  deleteVehicle,
  fetchVehicles,
  updateVehicle,
} from "@/apis/reservation";
import Button from "@/components/Button";
import AlertModal from "@/components/Modal/AlertModal";
import ConfirmModal from "@/components/Modal/ConfirmModal";
import type { Vehicle } from "@/types/vehicleReservation";

import VehicleEditModal, { type VehicleInput } from "./VehicleEditModal";

const emptyInput: VehicleInput = { vehicleName: "", vehicleNo: "" };

const VehicleMasterSection = () => {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // 추가/수정 모달
  const [editing, setEditing] = useState<{
    record: VehicleInput;
    isNew: boolean;
    editId: number | null;
  } | null>(null);
  // 삭제 확인 대상
  const [deleteTarget, setDeleteTarget] = useState<Vehicle | null>(null);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);

  const loadVehicles = useCallback(async () => {
    const list = await fetchVehicles();
    setVehicles(list);
  }, []);

  useEffect(() => {
    loadVehicles();
  }, [loadVehicles]);

  const openAdd = () =>
    setEditing({ record: { ...emptyInput }, isNew: true, editId: null });

  const openEdit = (v: Vehicle) =>
    setEditing({
      record: { vehicleName: v.vehicleName, vehicleNo: v.vehicleNo },
      isNew: false,
      editId: v.vhclId,
    });

  const handleSave = async (input: VehicleInput) => {
    if (saving || !editing) return;
    setSaving(true);
    try {
      if (editing.isNew) {
        await createVehicle(input);
        setAlertMessage("차량이 추가되었습니다.");
      } else if (editing.editId != null) {
        await updateVehicle(editing.editId, input);
        setAlertMessage("차량 정보가 수정되었습니다.");
      }
      setEditing(null);
      await loadVehicles();
    } catch (e) {
      console.error("차량 저장 실패:", e);
      setAlertMessage("저장에 실패했습니다. 다시 시도해주세요.");
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteVehicle(deleteTarget.vhclId);
      setAlertMessage("차량이 삭제되었습니다.");
      await loadVehicles();
    } catch (e) {
      console.error("차량 삭제 실패:", e);
      setAlertMessage("삭제에 실패했습니다. 다시 시도해주세요.");
    } finally {
      setDeleteTarget(null);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50/50">
      {/* 섹션 헤더 (접기/펼치기) */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-4 py-3"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">
          <AiOutlineCar size={18} />
          차량 목록 관리
          <span className="text-xs font-normal text-gray-400">
            ({vehicles.length}대)
          </span>
        </span>
        <span className="text-xs text-gray-400">{open ? "접기 ▲" : "펼치기 ▼"}</span>
      </button>

      {open && (
        <div className="flex flex-col gap-2 px-4 pb-4">
          {vehicles.length === 0 ? (
            <p className="py-2 text-sm text-gray-400">등록된 차량이 없습니다.</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {vehicles.map((v) => (
                <li
                  key={v.vhclId}
                  className="flex items-center justify-between rounded-md border border-gray-200 bg-white px-3 py-2"
                >
                  <span className="text-sm text-gray-700">
                    <span className="font-medium">{v.vehicleName}</span>
                    <span className="ml-2 text-gray-400">{v.vehicleNo}</span>
                  </span>
                  <div className="flex gap-1.5">
                    <Button
                      variant="custom"
                      onClick={() => openEdit(v)}
                      className="rounded-md border border-gray-200 px-2.5 py-1 text-xs text-gray-600 hover:bg-gray-50"
                    >
                      수정
                    </Button>
                    <Button
                      variant="custom"
                      onClick={() => setDeleteTarget(v)}
                      className="rounded-md border border-red-200 px-2.5 py-1 text-xs text-red-500 hover:bg-red-50"
                    >
                      삭제
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <Button
            variant="custom"
            onClick={openAdd}
            className="mt-1 flex items-center justify-center gap-1 rounded-md border border-dashed border-primary/40 px-3 py-2 text-sm font-medium text-primary hover:bg-primary/5"
          >
            <AiOutlinePlus size={16} />
            차량 추가
          </Button>
        </div>
      )}

      {/* 추가/수정 모달 */}
      {editing && (
        <VehicleEditModal
          record={editing.record}
          isNew={editing.isNew}
          onSave={handleSave}
          onClose={() => setEditing(null)}
        />
      )}

      {/* 삭제 확인 */}
      {deleteTarget && (
        <ConfirmModal
          title="차량 삭제"
          onConfirm={handleConfirmDelete}
          onClose={() => setDeleteTarget(null)}
        >
          '{deleteTarget.vehicleName}' 차량을 삭제하시겠습니까?
          <br />
          기존 예약 내역은 그대로 유지되며, 신규 예약 선택지에서만 제외됩니다.
        </ConfirmModal>
      )}

      {/* 알림 */}
      {alertMessage && (
        <AlertModal onClose={() => setAlertMessage(null)}>
          {alertMessage}
        </AlertModal>
      )}
    </div>
  );
};

export default VehicleMasterSection;
